import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ProductId, recommend } from './rules';
import { ChipStep, EMPTY_ANSWERS, QuizAnswers, QuizStep, RadioStep, STEPS, StepId } from './steps';

/** The two screens after the last question, which the progress bar does not count. */
export type QuizPhase = 'question' | 'calculating' | 'result';

/** How long a tapped radio row shows its selected state before the next question replaces it. */
const RADIO_ADVANCE_MS = 500;

type RadioStepId = Extract<StepId, 'age' | 'size' | 'residence'>;
type ChipStepId = Extract<StepId, 'exercise' | 'importance'>;

export interface QuizFlow {
  step: QuizStep;
  stepIndex: number;
  phase: QuizPhase;
  answers: QuizAnswers;
  /** Decided when the flow leaves the last question; null until then. */
  recommendation: ProductId | null;
  canGoBack: boolean;
  /** Whether the docked Next button on a chip or email step is enabled. */
  canAdvance: boolean;
  selectRadio(step: RadioStep<string>, optionId: string): void;
  toggleChip(step: ChipStep<string>, optionId: string): void;
  setEmail(email: string | null): void;
  next(): void;
  back(): void;
  restart(): void;
  /** Leaves the calculating screen for the result. */
  showResult(): void;
}

/**
 * Drives the six-question flow. Answers live in component state only: a reload
 * restarts the quiz, matching fi-quizz. Nothing here writes the fi_quiz cookie
 * or fires capture; the sheet does that once the flow reaches `result`.
 */
export default function useQuizFlow(): QuizFlow {
  const [stepIndex, setStepIndex] = useState(0);
  const [phase, setPhase] = useState<QuizPhase>('question');
  const [answers, setAnswers] = useState<QuizAnswers>(EMPTY_ANSWERS);

  const step = STEPS[stepIndex];
  const advanceTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(advanceTimer.current), []);

  // Not a setStepIndex updater: strict mode invokes updaters twice, and setPhase
  // inside one would fire the calculating screen on a re-render.
  const advance = useCallback(() => {
    if (stepIndex < STEPS.length - 1) {
      setStepIndex(stepIndex + 1);
      return;
    }
    setPhase('calculating');
  }, [stepIndex]);

  const selectRadio = useCallback(
    (radio: RadioStep<string>, optionId: string) => {
      setAnswers((a) => ({ ...a, [radio.id as RadioStepId]: optionId }));
      window.clearTimeout(advanceTimer.current);
      advanceTimer.current = window.setTimeout(advance, RADIO_ADVANCE_MS);
    },
    [advance],
  );

  // A pick past the cap is refused rather than rotating the oldest out, so the
  // visitor's earlier choices never move under them.
  const toggleChip = useCallback((chip: ChipStep<string>, optionId: string) => {
    setAnswers((a) => {
      const current = a[chip.id as ChipStepId] as string[];
      if (current.includes(optionId)) {
        return { ...a, [chip.id]: current.filter((id) => id !== optionId) };
      }
      return current.length >= chip.maxSelect ? a : { ...a, [chip.id]: [...current, optionId] };
    });
  }, []);

  const setEmail = useCallback((email: string | null) => setAnswers((a) => ({ ...a, email })), []);

  const back = useCallback(() => setStepIndex((i) => Math.max(0, i - 1)), []);

  const restart = useCallback(() => {
    setStepIndex(0);
    setPhase('question');
    setAnswers(EMPTY_ANSWERS);
  }, []);

  // Derived rather than stored on the way out of the last question: a stored
  // copy would be computed from a stale `answers` closure.
  const recommendation = useMemo(() => (phase === 'question' ? null : recommend(answers).best), [phase, answers]);

  const canAdvance = useMemo(() => {
    switch (step.kind) {
      case 'chip':
        return (answers[step.id as ChipStepId] as string[]).length > 0;
      case 'email':
        return true;
      default:
        return answers[step.id as RadioStepId] !== null;
    }
  }, [step, answers]);

  return {
    step,
    stepIndex,
    phase,
    answers,
    recommendation,
    canGoBack: stepIndex > 0 && phase === 'question',
    canAdvance,
    selectRadio,
    toggleChip,
    setEmail,
    next: advance,
    back,
    restart,
    showResult: () => setPhase('result'),
  };
}
